import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import ToastAlert, { ToastCloseIcon } from "../ToastAlert/ToastAlert.jsx";
import LoadingScreen from "../SSORequestAccountForm/components/LoadingScreen.jsx";
import PageBanner from "../PageBanner/PageBanner.jsx";
import PageBannerMockData from "../PageBanner/mockData.js";
import ContainerBleed from "../Container/ContainerBleed.jsx";
import ContainerFull from "../Container/ContainerFull.jsx";

const SSOAccountApproval = props => {
  const { hasUserGUID } = props;
  const [userGUID, setUserGUID] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState("active");

  const [userData, setUserData] = useState({});

  const statusToInt = {
    pending: 0,
    active: 1,
    inactive: 3,
    rejected: 4,
  };

  toast.onChange(payload => {
    console.log("Toast changed:", payload);
  });

  useEffect(() => {
    // this component exists on its own separate .aspx page
    // we need it to inherit VU styles, so we add the theme to body outselves
    document.body.classList.add("theme-vu");

    const urlParams = new URLSearchParams(window.location.search);
    const userGuidString = urlParams.get("user");

    if (userGuidString) {
      // Handle the userGuidString value
      setUserGUID(userGuidString);
      setLoading(true);
      fetch(
        `/sitecore/shell/B2C/AdminManagement/GetRequestUserByIdAsync?userGuidString=${userGuidString}`,
      )
        .then(response => {
          setLoading(false);
          if (response.status === 400) {
            toast(
              <ToastAlert
                style="error"
                title="Invalid User ID"
                description="The user ID provided is invalid. Please contact support."
              />,
              {
                className: "st-toast-alert",
                hideProgressBar: true,
                position: "bottom-right",
                closeButton: <ToastCloseIcon />,
              },
            );
          } else if (response.status === 500) {
            toast(
              <ToastAlert
                style="error"
                title="Database Error"
                description="There was a database connection failure. Please try again later."
              />,
              {
                className: "st-toast-alert",
                hideProgressBar: true,
                position: "bottom-right",
                closeButton: <ToastCloseIcon />,
              },
            );
          } else if (response.status === 200) {
            return response.json();
          } else {
            toast(
              <ToastAlert
                style="error"
                title="Database Error"
                description="There was a database connection failure. Please try again later."
              />,
              {
                className: "st-toast-alert",
                hideProgressBar: true,
                position: "bottom-right",
                closeButton: <ToastCloseIcon />,
              },
            );
          }
        })
        .then(userData => {
          if (userData) {
            console.log("Valid user received:", userData);
            // Persist successful response data to state for rendering
            setUserData(userData);
            setAccountStatus(() => {
              switch (userData.status) {
                case 0:
                  return "pending";
                case 1:
                  return "active";
                // case 2 = trial, not in use
                case 3:
                  return "inactive";
                case 4:
                  return "rejected";
                default:
                  return null;
              }
            });
          }
        })
        .catch(error => {
          console.error("Fetch error:", error);
          toast(
            <ToastAlert
              style="error"
              title="Network Error"
              description="Unable to connect to the server. Please try again."
            />,
            {
              className: "st-toast-alert",
              hideProgressBar: true,
              position: "bottom-right",
              closeButton: <ToastCloseIcon />,
            },
          );
        });
    } else {
      setUserGUID(null);
    }
  }, []);

  const updateAccountStatus = e => {
    e.preventDefault();
    // Function to update account status
    const formData = new FormData(e.target);
    const approvalStatus = formData.get("approval-status");
    const note = (formData.get("approval-note") || "").trim();

    // A note is required whenever the status actually changes, consistent with
    // the VU User Manager dashboard (the backend enforces this too).
    const statusChanged = approvalStatus !== accountStatus;
    if (statusChanged && !note) {
      toast(
        <ToastAlert
          style="error"
          title="Note Required"
          description="A note is required when changing the account status."
        />,
        {
          className: "st-toast-alert",
          hideProgressBar: true,
          position: "bottom-right",
          closeButton: <ToastCloseIcon />,
        },
      );
      return;
    }

    console.log("Updating account status to:", e);

    setLoading(true);
    setShowModal(false);
    fetch("/sitecore/shell/B2C/AdminManagement/User/ManageAsync", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        Email: userData.email,
        Source: userData.source,
        Status: statusToInt[approvalStatus],
        Note: note,
      }),
    })
      .then(response => {
        setLoading(false);
        if (response.ok) {
          setAccountStatus(approvalStatus);
          toast(
            <ToastAlert
              style="success"
              title="Success"
              description="Account status updated successfully."
            />,
            {
              className: "st-toast-alert",
              hideProgressBar: true,
              position: "bottom-right",
              closeButton: <ToastCloseIcon />,
            },
          );
          setShowModal(false);
        } else {
          throw new Error("Failed to update account status");
        }
      })
      .catch(error => {
        setLoading(false);
        console.error("Update error:", error);
        toast(
          <ToastAlert
            style="error"
            title="Update Failed"
            description="Unable to update account status. Please try again."
          />,
          {
            className: "st-toast-alert",
            hideProgressBar: true,
            position: "bottom-right",
            closeButton: <ToastCloseIcon />,
          },
        );
      });
  };

  return (
    <>
      <ContainerBleed backgroundColor="#f2f2f2">
        <PageBanner {...PageBannerMockData} title="Account Approval" />
      </ContainerBleed>
      {!userGUID && <div>No user found</div>}
      {userGUID && (
        <>
          {userData && Object.keys(userData).length > 0 && (
            <ContainerFull
              style={{ zIndex: 10, position: "relative", marginBlock: "4rem" }}
            >
              <div className="sso-account-approval">
                {loading && <LoadingScreen />}
                <div className="sso-account-approval-table">
                  <div className="sso-account-approval-table__header">
                    {window.isVuAdmin && (
                      <>
                        <a
                          target="_blank"
                          href="/sitecore/shell/Applications/VUAzureB2CAdminUploadUsers.aspx"
                        >
                          VU User Bulk Upload
                        </a>
                        <a
                          target="_blank"
                          href="/sitecore/shell/Applications/VUAzureB2CUserManager.aspx"
                        >
                          VU User Manager
                        </a>
                      </>
                    )}
                  </div>
                  <div className="sso-account-approval-table__wrapper">
                    <table className="sso-account-approval-table__table">
                      <thead>
                        <tr>
                          <th>Edit</th>
                          <th>Approval Status</th>
                          <th>Requested Date</th>
                          <th>First Name</th>
                          <th>Last Name</th>
                          <th>Email</th>
                          <th>Mobile Phone</th>
                          <th>Office Phone</th>
                          <th>City</th>
                          <th>State</th>
                          <th>ZIP Code</th>
                          <th>Company Name</th>
                          <th>Business Line</th>
                          <th>Doing Business In</th>
                          <th>Stewart Contact Name</th>
                          <th>Stewart Contact Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <i
                              className="fas fa-edit"
                              onClick={() => setShowModal(true)}
                            ></i>
                          </td>
                          <td className="approval-status">
                            {accountStatus ?? "unapproved"}
                          </td>
                          <td>
                            {userData.requestedDate
                              ? new Date(
                                  userData.requestedDate,
                                ).toLocaleString()
                              : ""}
                          </td>
                          <td>{userData.firstName}</td>
                          <td>{userData.lastName}</td>
                          <td>{userData.email}</td>
                          <td>{userData.mobilePhone}</td>
                          <td>{userData.officePhone}</td>
                          <td>{userData.city}</td>
                          <td>{userData.state}</td>
                          <td>{userData.zipCode}</td>
                          <td>{userData.companyName}</td>
                          <td>{userData.businessLine}</td>
                          <td>{userData.doingBusinessIn}</td>
                          <td>{userData.stewartContact}</td>
                          <td>{userData.stewartContactEmail}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {showModal && (
                  <div className="sso-account-approval__modal">
                    <div className="sso-account-approval__modal-content">
                      <i
                        class="far fa-times sso-account-approval__close-modal"
                        onClick={() => setShowModal(false)}
                      ></i>
                      <div className="sso-account-approval__modal-title txt-h3">
                        Individual Approval
                      </div>
                      <form onSubmit={updateAccountStatus}>
                        <ul>
                          <li>
                            <label for="approval--pending">
                              <input
                                type="radio"
                                id="approval--pending"
                                name="approval-status"
                                value="pending"
                                defaultChecked={accountStatus === "pending"}
                              />{" "}
                              Pending
                            </label>
                          </li>
                          <li>
                            <label for="approval--active">
                              <input
                                type="radio"
                                id="approval--active"
                                name="approval-status"
                                value="active"
                                defaultChecked={accountStatus === "active"}
                              />{" "}
                              Active
                            </label>
                          </li>
                          <li>
                            <label for="approval--inactive">
                              <input
                                type="radio"
                                id="approval--inactive"
                                name="approval-status"
                                value="inactive"
                                defaultChecked={accountStatus === "inactive"}
                              />{" "}
                              Inactive
                            </label>
                          </li>
                          <li>
                            <label for="approval--rejected">
                              <input
                                type="radio"
                                id="approval--rejected"
                                name="approval-status"
                                value="rejected"
                                defaultChecked={accountStatus === "rejected"}
                              />{" "}
                              Rejected
                            </label>
                          </li>
                        </ul>
                        <div className="sso-account-approval__note-add">
                          <label
                            htmlFor="approval-note"
                            className="sso-account-approval__rejection-note-label"
                          >
                            Note (required for status change)
                          </label>
                          <textarea
                            id="approval-note"
                            name="approval-note"
                            rows={3}
                            placeholder="Reason for this status change..."
                            className="sso-account-approval__note-add-input"
                          />
                        </div>
                        <button type="submit" className="btn btn--primary">
                          Submit
                        </button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </ContainerFull>
          )}
        </>
      )}
      <ToastContainer autoClose={false} />
    </>
  );
};

export default SSOAccountApproval;
